/** One invited club of a tournament, as `GET /association/tournament/list` lists it. */
export interface TournamentOverviewClub {
  clubId: number;
  clubNumberCountry: string;
  name: string | null;
  registered: boolean;
  /** Squad members filed for the tournament, substitutes counted separately. */
  players: number;
  substitutes: number;
  /** The squad confirmation PDF the club received, when the file still exists. */
  pdfUrl: string | null;
}

/** One row of the association's Meldungsübersicht. */
export interface TournamentOverviewRow {
  id: number;
  title: string;
  startDate: string; // dd.mm.yyyy
  endDate: string; // dd.mm.yyyy
  registrationStart: string; // dd.mm.yyyy HH:MM
  registrationEnd: string; // dd.mm.yyyy HH:MM
  venue: string | null;
  clubs: TournamentOverviewClub[];
}

/** A club offered as Veranstaltungsort or Teilnehmer. */
export interface TournamentClub {
  id: number;
  name: string;
  clubNumberCountry: string;
}

/** The stored tournament, as `GET /association/tournament/{id}` answers it. */
export interface TournamentFormData {
  id: number;
  title: string;
  organizerClubId: number;
  gameType: string | null;
  individualAllowed: boolean;
  sorting: string;
  startDate: string; // yyyy-mm-dd
  endDate: string; // yyyy-mm-dd
  price: string;
  registrationStart: string; // yyyy-mm-dd HH:MM:SS
  registrationEnd: string; // yyyy-mm-dd HH:MM:SS
  numberOfPlayers: number | null;
  numberOfSubstitutes: number | null;
  refereeEmail: string;
  gender: 'M' | 'W' | 'N' | null;
  birthDateFrom: string; // yyyy-mm-dd
  birthDateTo: string; // yyyy-mm-dd
  maxHandicap: string;
  participantClubIds: number[];
  /** Clubs that already filed a Meldung or players for this tournament. */
  registrations: TournamentRegistration[];
  /** False once any club has filed a Meldung or players for the tournament. */
  canDelete: boolean;
}

export interface TournamentRegistration {
  clubId: number | null;
  clubNumberCountry: string;
  players: number;
  substitutes: number;
}

/** What `POST /association/tournament/save` takes; `id` present means update. */
export interface TournamentSavePayload {
  id?: number;
  title: string;
  organizerClubId: number | '';
  gameType: string;
  individualAllowed: boolean;
  sorting: string;
  startDate: string;
  endDate: string;
  price: string;
  registrationStart: string;
  registrationEnd: string;
  numberOfPlayers: string;
  numberOfSubstitutes: string;
  refereeEmail: string;
  gender: string;
  birthDateFrom: string;
  birthDateTo: string;
  maxHandicap: string;
  participantClubIds: number[];
}

export const GAME_TYPES = [
  'Zählspiel-Einzel',
  'Zählspiel-Vierer',
  'Lochwettspiel-Einzel',
  'Lochwettspiel-Vierer',
] as const;

// The stored values are the column's enum and the legacy application orders
// players by them; only the labels say HCPI.
export const SORTINGS = [
  { value: 'STV aufsteigend', label: 'HCPI aufsteigend' },
  { value: 'STV absteigend', label: 'HCPI absteigend' },
  { value: 'frei', label: 'frei' },
] as const;

export const GENDERS = [
  { value: 'M', label: 'M' },
  { value: 'W', label: 'W' },
  { value: 'N', label: 'N (ohne Angabe)' },
] as const;

/** One PC CADDIE event at the venue club, as `event_get_list` answers it. */
export interface PccEvent {
  id: number | null;
  date: string | null;
  name: string | null;
  number: string | null;
  category: string | null;
}

/** `GET /association/tournament/{id}/pcc-events`: what the association confirms before sending. */
export interface PccTransferPreview {
  id: number;
  title: string;
  venue: string | null;
  venueClubNumberCountry: string | null;
  startDate: string;
  endDate: string;
  gameType: string | null;
  singleCompetition: boolean;
  playersTotal: number;
  clubs: {
    clubNumberCountry: string;
    name: string | null;
    players: number;
    substitutes: number;
  }[];
  /** 'ok' with no events: nothing on that date; 'unavailable': PC CADDIE did not answer. */
  eventsStatus: 'ok' | 'unavailable';
  eventsError: string | null;
  events: PccEvent[];
}

/** One player's answer from PC CADDIE after `pcc_cpentry`. */
export interface PccPlayerResult {
  msg?: string;
  code?: string;
  persid?: string;
  detail_string?: string;
  firstname?: string;
  lastname?: string;
  club_country_number?: string;
}

/** `POST /association/tournament/{id}/send-to-pcc` */
export interface PccTransferResult {
  status: 'success' | 'fail';
  message: string;
  eventNumber: string;
  playersSent: number;
  players: PccPlayerResult[];
  error: string | null;
}
